import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ModuleIconPicker } from '@/components/admin/ModuleIconPicker';
import type { CourseSection } from '@/lib/api';
import type { CreateSectionPayload, UpdateSectionPayload } from '@/lib/api';

const DEFAULT_ICON_KEY = 'book-open';

interface ModuleFormProps {
  initial?: CourseSection | null;
  onSave: (payload: CreateSectionPayload | UpdateSectionPayload) => Promise<void>;
  onCancel: () => void;
}

export function ModuleForm({ initial, onSave, onCancel }: ModuleFormProps) {
  const [title, setTitle] = useState(initial?.title ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [icon, setIcon] = useState(initial?.icon ?? DEFAULT_ICON_KEY);
  const [totalDuration, setTotalDuration] = useState(initial?.totalDuration ?? '');
  const [lessonCount, setLessonCount] = useState(0);
  const [sortOrder, setSortOrder] = useState(initial?.sortOrder ?? 0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      await onSave(
        initial
          ? { title, sortOrder, description: description || undefined, icon: icon || undefined, totalDuration: totalDuration || undefined }
          : { title, sortOrder, description: description || undefined, icon: icon || undefined, totalDuration: totalDuration || undefined, lessonCount: lessonCount > 0 ? lessonCount : undefined }
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && <p className="text-sm text-destructive">{error}</p>}
      <div>
        <label className="block text-sm font-medium text-foreground mb-1">Title</label>
        <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Introduction" required />
      </div>
      <div>
        <label className="block text-sm font-medium text-foreground mb-1">Description</label>
        <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} placeholder="Describe this module…" />
      </div>
      <div>
        <label className="block text-sm font-medium text-foreground mb-1">Icon</label>
        <ModuleIconPicker value={icon || DEFAULT_ICON_KEY} onChange={setIcon} />
      </div>
      <div>
        <label className="block text-sm font-medium text-foreground mb-1">Duration</label>
        <Input value={totalDuration} onChange={(e) => setTotalDuration(e.target.value)} placeholder="e.g. 2h, 30 min" />
      </div>
      {!initial && (
        <div>
          <label className="block text-sm font-medium text-foreground mb-1">Lesson count</label>
          <Input type="number" min={0} max={100} value={lessonCount} onChange={(e) => setLessonCount(Math.max(0, parseInt(e.target.value, 10) || 0))} placeholder="0" />
          <p className="text-xs text-muted-foreground mt-1">Number of new lessons to create for this module (editable in lesson view).</p>
        </div>
      )}
      <div>
        <label className="block text-sm font-medium text-foreground mb-1">Sort order</label>
        <Input type="number" value={sortOrder} onChange={(e) => setSortOrder(Number(e.target.value) || 0)} />
      </div>
      <div className="flex gap-2 pt-4">
        <Button type="submit" disabled={saving} className="bg-accent text-accent-foreground hover:bg-gold-light">
          {saving ? 'Saving…' : 'Save'}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
      </div>
    </form>
  );
}
